/**
 * AudioElementPool — Manages a pool of hidden <audio> elements for
 * multi-session WebRTC audio playback.
 *
 * Each concurrent Call_Session gets its own dedicated audio element
 * (up to a maximum of 4). The pool handles allocation, release,
 * hold/resume transitions, conference detach/reattach, and autoplay
 * failure recovery.
 */

export interface AudioElementPool {
  elements: Map<string, HTMLAudioElement>; // sessionId → audio element
  maxElements: number; // 4
  allocate(sessionId: string): HTMLAudioElement;
  release(sessionId: string): void;
  detachAll(): void; // During conference mode
  reattach(sessionId: string, stream: MediaStream): void;
}

export class AudioElementPoolImpl implements AudioElementPool {
  public elements: Map<string, HTMLAudioElement> = new Map();
  public readonly maxElements: number = 4;

  /**
   * Tracks sessions that need autoplay retry on next user interaction.
   * When play() fails due to browser autoplay policy, we keep srcObject
   * assigned and store the sessionId here for retry.
   */
  private pendingAutoplayRetry: Set<string> = new Set();

  /**
   * Bound handler for user interaction events (click/keypress) that
   * retries play() on elements that failed due to autoplay policy.
   */
  private userInteractionHandler: (() => void) | null = null;

  constructor() {
    this.setupAutoplayRetryListener();
  }

  /**
   * Allocate a hidden audio element for a session.
   * If the session already has an element, return the existing one.
   * If the pool is at capacity, throws an error.
   */
  allocate(sessionId: string): HTMLAudioElement {
    // Return existing element if already allocated for this session
    const existing = this.elements.get(sessionId);
    if (existing) return existing;

    if (this.elements.size >= this.maxElements) {
      console.warn(
        `[AudioElementPool] Cannot allocate — pool at max capacity (${this.maxElements})`
      );
      throw new Error(
        `Audio element pool at maximum capacity (${this.maxElements})`
      );
    }

    const audioEl = document.createElement("audio");
    audioEl.id = `session-audio-${sessionId}`;
    audioEl.autoplay = true;
    audioEl.setAttribute("playsinline", "");
    // Hide the element off-screen
    audioEl.style.position = "absolute";
    audioEl.style.top = "-9999px";
    audioEl.style.left = "-9999px";

    // Append to document body so it can play
    document.body.appendChild(audioEl);

    this.elements.set(sessionId, audioEl);
    return audioEl;
  }

  /**
   * Release an audio element from the pool.
   * Clears srcObject and removes the DOM element.
   */
  release(sessionId: string): void {
    const audioEl = this.elements.get(sessionId);
    if (!audioEl) return;

    // Stop playback and clear stream
    audioEl.pause();
    audioEl.srcObject = null;

    // Remove from DOM
    if (audioEl.parentNode) {
      audioEl.parentNode.removeChild(audioEl);
    }

    this.elements.delete(sessionId);
    this.pendingAutoplayRetry.delete(sessionId);
  }

  /**
   * Detach audio from a held session.
   * Sets srcObject to null so no audio is rendered for the held call.
   * (Requirement 7.2)
   */
  detachSession(sessionId: string): void {
    const audioEl = this.elements.get(sessionId);
    if (!audioEl) return;

    audioEl.srcObject = null;
    this.pendingAutoplayRetry.delete(sessionId);
  }

  /**
   * Detach all audio elements (for conference mode).
   * Sets all elements' srcObject to null.
   * (Design: detachAll for conference mode)
   */
  detachAll(): void {
    this.elements.forEach((audioEl, sessionId) => {
      audioEl.srcObject = null;
      this.pendingAutoplayRetry.delete(sessionId);
    });
  }

  /**
   * Reattach a remote MediaStream to a session's audio element and play.
   * Used when resuming a held call (Requirement 7.3: within 500ms).
   * On autoplay failure, retains srcObject and schedules retry (Requirement 7.6).
   */
  reattach(sessionId: string, stream: MediaStream): void {
    let audioEl = this.elements.get(sessionId);

    // If no element exists for this session, allocate one
    if (!audioEl) {
      try {
        audioEl = this.allocate(sessionId);
      } catch {
        console.error(
          `[AudioElementPool] Cannot reattach — failed to allocate element for session ${sessionId}`
        );
        return;
      }
    }

    audioEl.srcObject = stream;

    // Attempt playback
    const playPromise = audioEl.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch((error: Error) => {
        if (error.name === "NotAllowedError") {
          // Autoplay policy blocked playback (Requirement 7.6):
          // Keep srcObject assigned and reattempt on next user interaction
          console.warn(
            `[AudioElementPool] Autoplay blocked for session ${sessionId} — will retry on user interaction`
          );
          this.pendingAutoplayRetry.add(sessionId);
        } else {
          console.error(
            `[AudioElementPool] Play failed for session ${sessionId}:`,
            error.message
          );
        }
      });
    }
  }

  /**
   * Get an audio element for a session (if allocated).
   */
  getElement(sessionId: string): HTMLAudioElement | undefined {
    return this.elements.get(sessionId);
  }

  /**
   * Get the number of currently allocated elements.
   */
  getPoolSize(): number {
    return this.elements.size;
  }

  /**
   * Check if a session has a pending autoplay retry.
   */
  hasPendingAutoplay(sessionId: string): boolean {
    return this.pendingAutoplayRetry.has(sessionId);
  }

  /**
   * Clean up all elements and event listeners.
   * Call this when the provider unmounts.
   */
  destroy(): void {
    // Release all elements
    for (const sessionId of Array.from(this.elements.keys())) {
      this.release(sessionId);
    }

    // Remove user interaction listener
    this.removeAutoplayRetryListener();
  }

  // ---------- Private: Autoplay retry ----------

  /**
   * Set up event listeners on document for user interaction events
   * (click, keypress) to retry play() on elements that failed autoplay.
   */
  private setupAutoplayRetryListener(): void {
    this.userInteractionHandler = () => {
      if (this.pendingAutoplayRetry.size === 0) return;

      for (const sessionId of Array.from(this.pendingAutoplayRetry)) {
        const audioEl = this.elements.get(sessionId);
        if (!audioEl || !audioEl.srcObject) {
          // Element was released or srcObject cleared — no need to retry
          this.pendingAutoplayRetry.delete(sessionId);
          continue;
        }

        const playPromise = audioEl.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise
            .then(() => {
              this.pendingAutoplayRetry.delete(sessionId);
            })
            .catch(() => {
              // Still blocked — keep in retry set for next interaction
            });
        }
      }
    };

    document.addEventListener("click", this.userInteractionHandler);
    document.addEventListener("keypress", this.userInteractionHandler);
  }

  /**
   * Remove the user interaction event listeners.
   */
  private removeAutoplayRetryListener(): void {
    if (this.userInteractionHandler) {
      document.removeEventListener("click", this.userInteractionHandler);
      document.removeEventListener("keypress", this.userInteractionHandler);
      this.userInteractionHandler = null;
    }
  }
}
